/**
 * Dialog Stammdaten bearbeiten.
 * Aus pages/ProjektDetailPage.tsx herausgelöst (Plan Projekt-Tracking, Etappe 2) —
 * verschoben, nicht umgeschrieben.
 */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useProjektDetail, useProjektStatusOptions, useTeamOwners, useUpdateProjekt, useKunden, useProjektAuftragswert } from "../../hooks/usePlanung";
import type { Kunde } from "../../lib/types";
import { formatEur } from "../../lib/planer";
import { zahl } from "./seiteHilfen";

/* ══ Stammdaten bearbeiten ═══════════════════════════════════════════════ */

export function StammdatenDialog({ projektId, onClose }: { projektId: string; onClose: () => void }) {
  const detail = useProjektDetail(projektId);
  const { data: wert } = useProjektAuftragswert(projektId);
  const { data: kunden = [] } = useKunden();
  const { data: owners = [] } = useTeamOwners();
  const { data: statusOpts = [] } = useProjektStatusOptions();
  const update = useUpdateProjekt();

  const [f, setF] = useState({
    title: "", kunde_id: "", ort: "", status: "", verantwortlicher: "",
    projektart: "", prioritaet: "", start: "", nettowert: "", notes: "",
  });
  /**
   * Woher der Auftragswert kommt (Migration 146).
   *
   * „Auto" ist der Normalfall: Angebot und Rechnungen bestimmen den Wert, und
   * der Server schreibt ihn beim Festschreiben fort. „Manuell" ist eine
   * ausdrückliche Entscheidung — und genau deshalb sichtbar, statt eines
   * Feldes, das sich beschreiben lässt und beim nächsten Beleg still wieder
   * überschrieben wird.
   */
  const [manuell, setManuell] = useState(false);

  useEffect(() => {
    const d = detail.data;
    if (!d) return;
    setF({
      title: d.title || "",
      kunde_id: d.kunde_id || "",
      ort: d.ort || "",
      status: d.status || "",
      verantwortlicher: d.verantwortlicher || "",
      projektart: d.projektart || "",
      prioritaet: d.prioritaet || "",
      start: d.start || "",
      nettowert: d.nettowert != null ? String(d.nettowert) : "",
      notes: d.notes || "",
    });
  }, [detail.data]);

  useEffect(() => { if (wert) setManuell(wert.manuell); }, [wert]);

  const set = (k: string, v: string) => setF((s) => ({ ...s, [k]: v }));
  const netto = zahl(f.nettowert);
  /** Der aktuelle Status bleibt wählbar, auch wenn er nicht mehr im Katalog steht. */
  const statusAuswahl = !f.status || statusOpts.includes(f.status) ? statusOpts : [f.status, ...statusOpts];

  const speichern = () => {
    if (!f.title.trim() || !f.kunde_id) return;
    update.mutate(
      {
        id: projektId,
        patch: {
          title: f.title.trim(),
          kunde_id: f.kunde_id,
          ort: f.ort,
          status: f.status,
          verantwortlicher: f.verantwortlicher,
          projektart: f.projektart,
          prioritaet: f.prioritaet,
          start: f.start,
          notes: f.notes,
          nettowert_manuell: manuell,
          ...(netto != null ? { nettowert: netto } : {}),
        },
      },
      { onSuccess: onClose },
    );
  };

  return createPortal(
    <div className="vg-overlay" role="dialog" aria-modal="true">
      <button type="button" className="vg-overlay-backdrop" aria-label="Schließen" onClick={onClose} />
      <div className="vg-overlay-panel">
        <div className="vg-overlay-head">
          <h2 className="vg-overlay-title">Stammdaten bearbeiten</h2>
          <button type="button" className="vg-overlay-close" onClick={onClose} aria-label="Schließen">×</button>
        </div>
        <div className="vg-form-grid" style={{ marginTop: 12 }}>
          <label className="is-wide">
            Titel
            <input value={f.title} onChange={(e) => set("title", e.target.value)} />
          </label>
          <label className="is-wide">
            Kunde
            <select value={f.kunde_id} onChange={(e) => set("kunde_id", e.target.value)}>
              <option value="">— wählen —</option>
              {kunden.map((k: Kunde) => <option key={k.id} value={k.id}>{k.name}</option>)}
            </select>
          </label>
          <label>
            Status
            <select value={f.status} onChange={(e) => set("status", e.target.value)}>
              {statusAuswahl.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label>
            Projektart
            <select value={f.projektart} onChange={(e) => set("projektart", e.target.value)}>
              <option value="bau">Bauprojekt</option>
              <option value="pflege">Pflegeprojekt</option>
            </select>
          </label>
          <label>
            Ort
            <input value={f.ort} onChange={(e) => set("ort", e.target.value)} />
          </label>
          <label>
            Start
            <input type="date" value={f.start} onChange={(e) => set("start", e.target.value)} />
          </label>
          <label>
            Verantwortlicher
            <select value={f.verantwortlicher} onChange={(e) => set("verantwortlicher", e.target.value)}>
              <option value="">— wählen —</option>
              {owners.map((o) => <option key={o.id} value={o.name}>{o.name}</option>)}
            </select>
          </label>
          <label>
            Priorität
            <select value={f.prioritaet} onChange={(e) => set("prioritaet", e.target.value)}>
              <option value="">— keine —</option>
              <option value="Niedrig">Niedrig</option>
              <option value="Normal">Normal</option>
              <option value="Hoch">Hoch</option>
              <option value="Dringend">Dringend</option>
            </select>
          </label>
          <div className="vg-feld-wert">
            <span className="vg-wertmodus-kopf">
              Nettowert (€)
              <span className="vg-wertmodus" role="group" aria-label="Woher der Auftragswert kommt">
                <button
                  type="button"
                  className={manuell ? "" : "is-an"}
                  aria-pressed={!manuell}
                  onClick={() => setManuell(false)}
                >
                  Auto
                </button>
                <button
                  type="button"
                  className={manuell ? "is-an" : ""}
                  aria-pressed={manuell}
                  onClick={() => {
                    // Den aktuell gültigen Wert als Startpunkt übernehmen:
                    // bei 0 anzufangen wäre kein Umschalten, sondern ein
                    // Löschen mit Zusatzschritt.
                    setManuell(true);
                    if (wert && !f.nettowert.trim()) set("nettowert", String(wert.auftragswert));
                  }}
                >
                  Manuell
                </button>
              </span>
            </span>
            <input
              value={f.nettowert}
              onChange={(e) => set("nettowert", e.target.value)}
              inputMode="decimal"
              readOnly={!manuell}
              aria-label="Nettowert in Euro"
              title={manuell
                ? "Von Hand gesetzt — der Belegstand überschreibt diesen Wert nicht."
                : "Wird aus Angebot und Rechnungen gerechnet. Zum Ändern auf „Manuell“ stellen."}
            />
            <small className="vg-wertmodus-hinweis">
              {manuell
                ? `Von Hand gesetzt${wert ? ` · automatisch wären ${formatEur(wert.auto_wert)}` : ""}`
                : wert?.herkunft_text || "Wird aus Angebot und Rechnungen gerechnet."}
            </small>
          </div>
          <label className="is-wide">
            Notizen
            <textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} rows={2} />
          </label>
        </div>
        {update.isError && <div className="vg-banner is-warn" style={{ marginTop: 12 }}>Konnte nicht gespeichert werden.</div>}
        <div className="vg-overlay-actions">
          <button type="button" className="vg-btn vg-btn-ghost" onClick={onClose}>Abbrechen</button>
          <button
            type="button"
            className="vg-btn vg-btn-primary"
            onClick={speichern}
            disabled={!f.title.trim() || !f.kunde_id || update.isPending}
          >
            {update.isPending ? "Speichert…" : "Speichern"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
